(() => {
  if (customElements.get('bundle-items')) {
    return;
  }

  /**
   * Bundle Items component.
   */
  class BundleItems extends HTMLElement {
    constructor() {
      super();
      this.selectedItems = [];
      this.discounts = JSON.parse(this.querySelector('[data-discounts]').textContent);
      this.moneyFormat = this.dataset.moneyFormat;
    }

    /**
     * Add item.
     *
     * @param {Event} event
     */
    addItem(event) {
      const bundleItem = event.target.closest('[data-bundle-item]');
      const qtyInput = bundleItem.querySelector('input[type="number"]');
      qtyInput.value = 1;
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Handle change.
     *
     * @param {Event} event
     */
    handleChange(event) {
      this.updateQty(event);
      this.updateBar();
      this.updatePrice();
      this.updateButton();
    }

    /**
     * Get items quantity.
     *
     * @returns {Number}
     */
    getItemsQty() {
      return this.selectedItems.reduce((acc, item) => {
        return acc + Number(item.qty);
      }, 0);
    }

    /**
     * Update bar.
     *
     * @returns {Void}
     */
    updateBar() {
      if (!this.shippingBar) {
        return;
      }

      const itemsQty = this.getItemsQty();
      const bundlePrice = this.selectedItems.reduce((acc, item) => {
        const itemPrice = Number(item.price) * Number(item.qty);
        return acc + itemPrice;
      }, 0);

      this.shippingBar.setAttribute('data-total-price', bundlePrice);
      this.shippingBar.setAttribute('data-total-items', itemsQty);
      this.shippingBar.init();
    }

    /**
     * Update delivery threshold with discounted price.
     *
     * @param {Number} discountedPrice - Price after discount in cents
     * @returns {Void}
     */
    updateDeliveryThreshold(discountedPrice) {
      if (!this.shippingBar) {
        return;
      }

      const deliveryElement = this.shippingBar.querySelector('[data-progress-bar-delivery-price]');
      if (!deliveryElement) {
        return;
      }

      const deliveryPrice = parseFloat(deliveryElement.dataset.progressBarDeliveryPrice);
      if (!deliveryPrice) {
        return;
      }

      // Convertir le prix en centimes pour la comparaison
      const deliveryPriceInCents = deliveryPrice * 100;

      // Vérifier si le seuil est atteint avec le prix après discount
      if (discountedPrice >= deliveryPriceInCents) {
        deliveryElement.classList.add('filled');
      } else {
        deliveryElement.classList.remove('filled');
      }
    }

    /**
     * Update button.
     *
     * @returns {Void}
     */
    updateButton() {
      const itemsQty = this.selectedItems.reduce((acc, item) => {
        return acc + Number(item.qty);
      }, 0);

      const atcButton = this.closest('main-product').querySelector('[name="add"]');
      if (!atcButton) {
        return;
      }
      // Mettre    jour le label du bouton si disponible
      const labelSpan = atcButton.querySelector('.product-selector__submit-label');
      if (labelSpan) {
        labelSpan.textContent =
          itemsQty > 0 ? (window?.variantStrings?.addToCart) : (window?.variantStrings?.unavailable);
      }

      if (itemsQty > 0) {
        atcButton.removeAttribute('disabled');
        atcButton.setAttribute('aria-disabled', 'false');
        return;
      }

      atcButton.setAttribute('disabled', '');
      atcButton.setAttribute('aria-disabled', 'true');
    }

    /**
     * Apply discounts.
     *
     * @param {Number} price
     * @returns {Number} - Shopify formatted price. Eg: 1000
     */
    applyDiscounts(price) {
      if (!this.shippingBar) {
        return price;
      }

      const reachedThresholdIndex = this.shippingBar.getReachedThreshold().index;
      if (reachedThresholdIndex === -1) {
        return price;
      }
      const discount = this.discounts[reachedThresholdIndex];
      const { discount_type: type, discount_value: value } = discount;
      if (value === 0) {
        return price;
      }

      if (type === 'percent') {
        const discountAmount = (value / 100) * price;
        return price - discountAmount;
      }
      const discountAmount = value * 100;
      return price - discountAmount;
    }

    /**
     * Update info prices.
     *
     * @param {Number} price - Shopify formatted price. Eg: 1000
     * @returns {Void}
     */
    updateInfo(price) {
      const count = this.querySelector('[data-count]');
      const singlePiece = this.querySelector('[data-single-piece]');
      const singleProduct = this.querySelector('[data-single-product]');

      if (!count || !singlePiece || !singleProduct) {
        return;
      }

      const itemsQty = this.getItemsQty();
      const singleProductPrice = price / this.getItemsQty();
      const singlePiecePrice = singleProductPrice / 20;

      count.innerText = itemsQty;
      singlePiece.innerText = Shopify.formatMoney(singlePiecePrice, this.moneyFormat);
      singleProduct.innerText = Shopify.formatMoney(singleProductPrice, this.moneyFormat);
    }

    /**
     * Update price.
     *
     * @returns {Void}
     */
    updatePrice() {
      const bundlePrice = this.selectedItems.reduce((acc, item) => {
        const itemPrice = Number(item.price) * Number(item.qty);
        return acc + itemPrice;
      }, 0);

      const discountedPrice = this.applyDiscounts(bundlePrice);
      const formattedPrice = Shopify.formatMoney(discountedPrice, this.moneyFormat);

      this.updateInfo(discountedPrice);
      this.updateDeliveryThreshold(discountedPrice);

      // Update subscription widget prices
      const subscriptionPriceElement = document.querySelector('.ax-subscription-purchase--price');
      const subscriptionPercentageElement =
        subscriptionPriceElement?.getAttribute('data-percentage');
      const subscriptionComparePriceElement = document.querySelector(
        '.ax-subscription-purchase--compare-price'
      );

      // Calculate subscription price with discount
      const subscriptionDiscount = subscriptionPercentageElement
        ? parseFloat(subscriptionPercentageElement) / 100
        : 0;

      const bundlePriceSubscriptionWithDiscount = this.selectedItems.reduce((acc, item) => {
        const rawPrice = Number(item.price) * (1 - subscriptionDiscount);
        const itemPrice = rawPrice * Number(item.qty);
        const roundedCents = Math.round(itemPrice);
        // Force proper rounding using toFixed and parseFloat
        const subscriptionPrice = parseFloat(roundedCents.toFixed(2));
        return acc + subscriptionPrice;
      }, 0);

      const formattedSubscriptionPrice = Shopify.formatMoney(
        bundlePriceSubscriptionWithDiscount,
        this.moneyFormat
      );

      if (subscriptionPriceElement) {
        subscriptionPriceElement.innerHTML = formattedSubscriptionPrice;
      }

      if (subscriptionComparePriceElement) {
        const bundlePriceFormatted = Shopify.formatMoney(bundlePrice, this.moneyFormat);
        subscriptionComparePriceElement.innerHTML = bundlePriceFormatted;
      }

      // Update one-time purchase prices
      const oneTimePriceElement = document.querySelector('.ax-one-time-purchase--price');
      const oneTimeComparePriceElement = document.querySelector(
        '.ax-one-time-purchase--compare-price'
      );

      if (oneTimePriceElement) {
        oneTimePriceElement.innerHTML = formattedPrice;
      }

      if (oneTimeComparePriceElement && bundlePrice > discountedPrice) {
        const bundlePriceFormatted = Shopify.formatMoney(bundlePrice, this.moneyFormat);
        oneTimeComparePriceElement.innerHTML = bundlePriceFormatted;
      } else if (oneTimeComparePriceElement) {
        oneTimeComparePriceElement.innerHTML = '';
      }

      const priceContainers = this.closest('main-product').querySelectorAll('product-button-price');
      if (!priceContainers.length) {
        return;
      }

      // Check which purchase type is selected
      const subscriptionInput = document.querySelector(
        'input[name="purchase_type"][value="subscription"]'
      );
      const isSubscription = subscriptionInput?.checked || false;

      priceContainers.forEach((priceContainer) => {
        if (isSubscription) {
          // Show subscription price
          if (bundlePriceSubscriptionWithDiscount === bundlePrice) {
            priceContainer.innerHTML = formattedSubscriptionPrice;
          } else {
            const bundlePriceFormatted = Shopify.formatMoney(bundlePrice, this.moneyFormat);
            priceContainer.innerHTML = `
              <ins>${formattedSubscriptionPrice}</ins>
              <del>${bundlePriceFormatted}</del>
            `;
          }
        } else {
          // Show one-time purchase price
          if (discountedPrice === bundlePrice) {
            priceContainer.innerHTML = formattedPrice;
          } else {
            const bundlePriceFormatted = Shopify.formatMoney(bundlePrice, this.moneyFormat);
            priceContainer.innerHTML = `
              <ins>${formattedPrice}</ins>
              <del>${bundlePriceFormatted}</del>
            `;
          }
        }
      });

      // Update product-bar__price
      const productBarPrice = this.closest('main-product').querySelector('.product-bar__price');
      if (productBarPrice) {
        if (isSubscription) {
          // Show subscription price
          if (bundlePriceSubscriptionWithDiscount === bundlePrice) {
            // Prix sans discount
            productBarPrice.innerHTML = `
              <div class="price">
                <div class="price__container">
                  <div class="price__regular">
                    <span>${formattedSubscriptionPrice}</span>
                  </div>
                </div>
              </div>
            `;
          } else {
            // Prix avec discount
            const bundlePriceFormatted = Shopify.formatMoney(bundlePrice, this.moneyFormat);
            productBarPrice.innerHTML = `
              <div class="price price--on-sale">
                <div class="price__container">
                  <div class="price__sale">
                    <del>${bundlePriceFormatted}</del>
                    <ins>${formattedSubscriptionPrice}</ins>
                  </div>
                </div>
              </div>
            `;
          }
        } else {
          // Show one-time purchase price
          if (discountedPrice === bundlePrice) {
            // Prix sans discount
            productBarPrice.innerHTML = `
              <div class="price">
                <div class="price__container">
                  <div class="price__regular">
                    <span>${formattedPrice}</span>
                  </div>
                </div>
              </div>
            `;
          } else {
            // Prix avec discount
            const bundlePriceFormatted = Shopify.formatMoney(bundlePrice, this.moneyFormat);
            productBarPrice.innerHTML = `
              <div class="price price--on-sale">
                <div class="price__container">
                  <div class="price__sale">
                    <del>${bundlePriceFormatted}</del>
                    <ins>${formattedPrice}</ins>
                  </div>
                </div>
              </div>
            `;
          }
        }
      }
    }

    /**
     * Update quantity.
     *
     * @param {Event} event
     * @returns {Void}
     */
    updateQty(event) {
      const bundleItem = event.target.closest('[data-bundle-item]');
      const qty = Number(bundleItem.querySelector('input[type="number"]').value);

      if (qty > 0) {
        bundleItem.classList.add('is-selected');
        const itemIndex = this.selectedItems.findIndex(
          (item) => item.id === bundleItem.dataset.bundleItem
        );
        if (itemIndex === -1) {
          this.selectedItems.push({
            id: bundleItem.dataset.bundleItem,
            productId: bundleItem.dataset.bundleItemProductId,
            qty: qty,
            price: bundleItem.dataset.bundleItemPrice,
          });
          return;
        }
        this.selectedItems[itemIndex].qty = qty;
        return;
      }

      bundleItem.classList.remove('is-selected');

      const itemIndex = this.selectedItems.findIndex(
        (item) => item.id === bundleItem.dataset.bundleItem
      );
      if (itemIndex !== -1) {
        this.selectedItems.splice(itemIndex, 1);
      }
    }

    /**
     * Show hidden items.
     */
    showHiddenItems() {
      const hiddenItems = this.querySelectorAll(
        '[data-bundle-item].small-hide, [data-bundle-item].large-up-hide'
      );
      hiddenItems.forEach((item) => {
        item.classList.remove('small-hide', 'large-up-hide', 'medium-hide');
      });

      this.querySelector('[data-show-more]')?.classList.add('hidden');
    }

    /**
     * Connected callback.
     *
     * @returns {Void}
     */
    connectedCallback() {
      this.bundleItems = this.querySelectorAll('[data-bundle-item]');

      if (this.bundleItems.length === 0) {
        return;
      }
      [...this.bundleItems].forEach((item) => {
        item.addEventListener('change', this.handleChange.bind(this));
        item.querySelector('[data-add]').addEventListener('click', this.addItem.bind(this));
      });

      this.shippingBar = this.parentElement.querySelector('shipping-bar');
      this.querySelector('[data-show-more]')?.addEventListener(
        'click',
        this.showHiddenItems.bind(this)
      );
    }
  }

  customElements.define('bundle-items', BundleItems);
})();
